import { useMutation } from '@apollo/client';
import { faCircle } from '@fortawesome/pro-solid-svg-icons';
import { format } from 'date-fns';
import * as React from 'react';
import { useState } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';

import Button from '../../../core/components/Button.tsx';
import ConfirmModal from '../../../core/components/ConfirmModal.tsx';
import SelectField from '../../../core/components/Fields/SelectField.tsx';
import TagsField from '../../../core/components/Fields/TagsField.tsx';
import FormField from '../../../core/components/FormField.tsx';
import { withIcon } from '../../../core/components/Icon.tsx';
import SaveStatus from '../../../core/components/SaveStatus.tsx';
import { SettingsCard } from '../../../core/components/SettingsFormCard.tsx';
import { useToaster } from '../../../core/components/Toast.tsx';
import { useApplicationContext } from '../../../core/contexts/ApplicationContext.tsx';
import classnames from '../../../core/utils/classnames.tsx';
import countFieldErrors from '../../../core/utils/countFieldErrors.ts';
import { DELETE_SMART_INBOX_INTEGRATION } from '../../../customer/queries.ts';
import {
    ConnectionAction,
    ConnectionStatus,
    emailsAndDomainsRegex,
    getAvailableActions,
    getPersonalSmartInboxConnectionStatus,
} from '../../../document/constants.ts';
import { PersonalSmartInboxIntegrationType } from '../../../users/components/PersonalSmartInbox/types.ts';
import { PERSONAL_SMART_INBOX_INTEGRATIONS, SETUP_PERSONAL_MAILBOX } from '../../../users/queries.ts';
import { canManageSmartInbox } from '../../../users/utils.ts';
import { OVERVIEW_PATH } from '../constants.ts';

const DotIcon = withIcon(faCircle);

const cardTitle = (text: React.ReactNode) => <span className="text-base font-semibold">{text}</span>;
const cardDescription = (text: React.ReactNode) => <span className="text-sm">{text}</span>;

interface ExcludedContentForm {
    keywords: string[];
    emailsDomains: string[];
}

interface SettingsTabPersonalProps {
    integration: PersonalSmartInboxIntegrationType;
    onDisconnected: () => void;
}

const SettingsTabPersonal = ({ integration, onDisconnected }: SettingsTabPersonalProps) => {
    const { t } = useTranslation('mailboxes');
    const navigate = useNavigate();
    const { publishToast } = useToaster();
    const [isDisconnecting, setIsDisconnecting] = useState(false);
    const [statusChanging, setStatusChanging] = useState(false);

    const [setupPersonalMailbox] = useMutation(SETUP_PERSONAL_MAILBOX, {
        refetchQueries: [{ query: PERSONAL_SMART_INBOX_INTEGRATIONS }],
    });
    const [deleteIntegration, { loading: disconnecting }] = useMutation(DELETE_SMART_INBOX_INTEGRATION);

    const excludedContentForm = useForm<ExcludedContentForm>({
        defaultValues: {
            keywords: integration.manualProcessingKeywords || [],
            emailsDomains: integration.manualProcessingSenderEmails || [],
        },
        mode: 'onChange',
    });

    // the channels picked in the setup, read-only: setting them up again only adds channels, it never removes one
    const connectedChannels = [
        { label: t('detail.settingsTab.channels.order'), name: integration.orderChannel?.name },
        { label: t('detail.settingsTab.channels.rfq'), name: integration.rfqChannel?.name },
    ].filter(({ name }) => !!name);

    const currentStatus = getPersonalSmartInboxConnectionStatus(integration);
    const { user } = useApplicationContext();
    // disconnecting needs the shared Smart Inbox permission in the API, even for your own mailbox
    const availableActions = getAvailableActions(currentStatus).filter(
        (action) => action !== ConnectionAction.Disconnect || canManageSmartInbox(user)
    );

    const handleStatusAction = async (action: ConnectionAction) => {
        if (action === ConnectionAction.Disconnect) {
            setIsDisconnecting(true);
            return;
        }
        setStatusChanging(true);
        try {
            await setupPersonalMailbox({
                variables: { integrationId: integration.id, enabled: action === ConnectionAction.Resume },
            });
            publishToast({
                description: t(
                    action === ConnectionAction.Resume
                        ? 'detail.settingsTab.resumeSuccess'
                        : 'detail.settingsTab.pauseSuccess'
                ),
                status: 'success',
            });
        } catch (error) {
            console.error(error);
            publishToast({ description: t('detail.settingsTab.saveError'), status: 'error' });
        } finally {
            setStatusChanging(false);
        }
    };

    const handleDisconnect = async () => {
        try {
            await deleteIntegration({ variables: { id: integration.id } });
            publishToast({ description: t('detail.settingsTab.disconnectSuccess'), status: 'success' });
            onDisconnected();
        } catch (error) {
            console.error(error);
            publishToast({ description: t('detail.settingsTab.saveError'), status: 'error' });
        } finally {
            setIsDisconnecting(false);
        }
    };

    const handleSaveExcludedContent = async (formData: ExcludedContentForm) => {
        try {
            await setupPersonalMailbox({
                variables: {
                    integrationId: integration.id,
                    manualProcessingKeywords: formData.keywords ?? [],
                    manualProcessingSenderEmails: formData.emailsDomains ?? [],
                },
            });
            // nothing refetches this form, so the saved values become its clean state here
            excludedContentForm.reset(formData);
            publishToast({ description: t('detail.settingsTab.saveSuccess'), status: 'success' });
        } catch (error) {
            console.error(error);
            publishToast({ description: t('detail.settingsTab.saveError'), status: 'error' });
        }
    };

    return (
        <div className="flex flex-col gap-6">
            <SettingsCard
                className="max-w-none"
                title={cardTitle(t('detail.settingsTab.connectionTitle'))}
                description={cardDescription(t('detail.settingsTab.connectionDescription'))}
                controls={
                    <SelectField
                        isLoading={statusChanging}
                        value={currentStatus}
                        options={availableActions.map((a) => ({ label: a, value: a }))}
                        required
                        className={classnames(
                            currentStatus === ConnectionStatus.Connected && 'bg-surface-brand-medium-default'
                        )}
                        renderOption={(option) => (
                            <span className="text-content-strong">
                                {t(`detail.settingsTab.connectionAction.${option.label.toLowerCase()}`)}
                            </span>
                        )}
                        renderValue={() => (
                            <span className="text-content-strong font-medium text-sm flex items-center">
                                <DotIcon
                                    className={classnames(
                                        'text-[10px] pr-2 pb-[1px]',
                                        currentStatus === ConnectionStatus.Connected && 'text-content-success-weak',
                                        currentStatus === ConnectionStatus.Paused && 'text-content-warning-weak',
                                        currentStatus === ConnectionStatus.Error && 'text-content-danger-weak'
                                    )}
                                />
                                {t(`detail.settingsTab.connectionStatus.${currentStatus.toLowerCase()}`)}
                            </span>
                        )}
                        onValueChange={(action) => handleStatusAction(action as ConnectionAction)}
                    />
                }
            >
                {integration.connectionFailedAt ? (
                    <span className="text-sm text-content-danger-strong">
                        {t('detail.settingsTab.connectionErrorHint', {
                            date: format(new Date(integration.connectionFailedAt), 'PP'),
                        })}
                    </span>
                ) : (
                    <span />
                )}
            </SettingsCard>

            <SettingsCard
                className="max-w-none"
                title={cardTitle(t('detail.settingsTab.channels.title'))}
                description={cardDescription(t('detail.settingsTab.channels.description'))}
            >
                {connectedChannels.length > 0 ? (
                    <dl className="m-0 flex flex-col gap-2 text-sm">
                        {connectedChannels.map(({ label, name }) => (
                            <div key={label} className="flex gap-4">
                                <dt className="w-40 flex-none text-content-medium">{label}</dt>
                                <dd className="m-0 text-content-strong">{name}</dd>
                            </div>
                        ))}
                    </dl>
                ) : (
                    <span className="text-sm text-content-medium">{t('detail.settingsTab.channels.empty')}</span>
                )}
            </SettingsCard>

            <form onSubmit={excludedContentForm.handleSubmit(handleSaveExcludedContent)}>
                <SettingsCard
                    className="max-w-none"
                    title={cardTitle(t('detail.settingsTab.excludedContentTitle'))}
                    description={cardDescription(t('detail.settingsTab.skipAutomationDescription'))}
                >
                    <Controller
                        name="keywords"
                        control={excludedContentForm.control}
                        render={({ field }) => (
                            <FormField label={t('detail.settingsTab.keywords')}>
                                <TagsField
                                    value={field.value || []}
                                    onValueChange={field.onChange}
                                    placeholder={t('detail.settingsTab.keywordsPlaceholder')}
                                />
                            </FormField>
                        )}
                    />
                    <Controller
                        name="emailsDomains"
                        control={excludedContentForm.control}
                        rules={{
                            validate: (tags: string[]) =>
                                (tags || []).every((tag) => emailsAndDomainsRegex.test(tag)) ||
                                t('detail.settingsTab.invalidEmailOrDomain'),
                        }}
                        render={({ field, fieldState }) => (
                            <FormField label={t('detail.settingsTab.emailsOrDomains')} error={fieldState.error}>
                                <TagsField
                                    value={field.value || []}
                                    onValueChange={field.onChange}
                                    placeholder={t('detail.settingsTab.emailsOrDomainsPlaceholder')}
                                />
                            </FormField>
                        )}
                    />
                    <SaveStatus
                        isDirty={excludedContentForm.formState.isDirty}
                        isSubmitting={excludedContentForm.formState.isSubmitting}
                        errorCount={countFieldErrors(excludedContentForm.formState.errors)}
                        isDisabled={!excludedContentForm.formState.isValid}
                    >
                        <Button type="button" variant="outline" onClick={() => navigate(OVERVIEW_PATH)}>
                            {t('detail.settingsTab.cancel')}
                        </Button>
                    </SaveStatus>
                </SettingsCard>
            </form>

            {isDisconnecting && (
                <ConfirmModal
                    onConfirm={handleDisconnect}
                    onCancel={() => setIsDisconnecting(false)}
                    title={t('detail.settingsTab.disconnectConfirmTitle')}
                    labelConfirm={t('detail.settingsTab.disconnect')}
                    confirmDisabled={disconnecting}
                    danger
                >
                    {t('detail.settingsTab.disconnectConfirmMessage', { mailbox: integration.mailInbox })}
                </ConfirmModal>
            )}
        </div>
    );
};

export default SettingsTabPersonal;
